import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { PageHeader, PageBody } from "@/components/ui-bits";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function Insights() {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadBriefing = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai/insights");
      setBriefing(data);
    } catch (e) {
      toast.error("Could not get briefing");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadBriefing(); }, []);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!draft.trim() || sending) return;
    const myMsg = draft.trim();
    setDraft("");
    setMessages(m => [...m, { role: "user", text: myMsg }]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat", { message: myMsg });
      setMessages(m => [...m, { role: "assistant", text: data.response }]);
    } catch (e) {
      toast.error("AI is unreachable");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="insights-page">
      <PageHeader
        eyebrow="// AI"
        title="AI insights"
        description="Claude Sonnet 4.5 reads a numerical snapshot of your fleet and predicts events."
        actions={
          <button onClick={loadBriefing} data-testid="refresh-briefing-btn" className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-900 text-sm font-semibold hover:bg-neutral-900 hover:text-white">
            <RefreshCw className="w-4 h-4" /> Refresh briefing
          </button>
        }
      />
      <PageBody>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-px bg-neutral-200 border border-neutral-200">
          {/* Briefing */}
          <div className="lg:col-span-3 bg-white p-6">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#002FA7]" />
              <div className="label-eyebrow text-neutral-500">// DAILY BRIEFING</div>
            </div>
            {loading ? (
              <div className="mt-4 text-sm text-neutral-500">Analysing your fleet…</div>
            ) : briefing ? (
              <article className="mt-4 prose prose-sm max-w-none text-[#0A0A0A] leading-relaxed">
                <pre className="whitespace-pre-wrap font-body text-[15px]" data-testid="briefing-text">{briefing.briefing}</pre>
                <details className="mt-6">
                  <summary className="label-eyebrow text-neutral-500 cursor-pointer">// numerical snapshot</summary>
                  <pre className="mt-2 bg-neutral-50 border border-neutral-200 p-3 text-xs font-mono whitespace-pre-wrap">{briefing.summary_input}</pre>
                </details>
              </article>
            ) : (
              <div className="mt-4 text-sm text-neutral-500">No data.</div>
            )}
          </div>

          {/* Chat */}
          <div className="lg:col-span-2 bg-[#F4F4F5] p-6 flex flex-col h-[640px]">
            <div className="label-eyebrow text-neutral-500">// ASK CLAUDE</div>
            <div ref={scrollRef} className="flex-1 mt-3 overflow-y-auto scroll-thin space-y-3 pr-1">
              {messages.length === 0 && (
                <div className="text-sm text-neutral-500">
                  Try: &ldquo;Which devices look risky tomorrow?&rdquo;, &ldquo;Any temperature outliers?&rdquo;, &ldquo;Summarise anomalies this hour.&rdquo;
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`max-w-[90%] p-3 text-sm ${m.role === "user" ? "ml-auto bg-[#002FA7] text-white" : "bg-white border border-neutral-200"}`} data-testid={`msg-${m.role}-${i}`}>
                  {m.text}
                </div>
              ))}
              {sending && <div className="text-xs text-neutral-500 font-mono">Claude is thinking…</div>}
            </div>
            <div className="mt-3 flex gap-2">
              <Input
                data-testid="chat-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about your fleet…"
                className="rounded-sm bg-white"
              />
              <button onClick={send} data-testid="chat-send-btn" className="px-3 bg-[#002FA7] text-white hover:bg-[#001E70]" disabled={sending}>
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </PageBody>
    </div>
  );
}
